import React, { useState } from "react";
import {
    Card,
    Checkbox,
    Radio,
    Alert,
    Button,
    Select,
    Input,
    DatePicker,
    Form,
    Space
} from "antd";
import { CreditCardOutlined, DollarOutlined, BankOutlined } from "@ant-design/icons";


const harvestOptions = ["January", "April", "July", "October", "All"];
const paymentMethods = ["Credit Card", "PayPal", "Bank Transfer"];

export default function HarvestBillingAndPaymentMethodSelector() {
    const [harvestMonths, setHarvestMonths] = useState([]);
    const [billingType, setBillingType] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [form] = Form.useForm();

    const nextHarvest = harvestMonths.length > 0 ? harvestMonths[0] : "your next season";
    const billingLabel = {
        monthly: "Monthly billing – pay every month",
        perSeason: "Per Season billing – pay once each growing season",
        postHarvest: `Post-Harvest billing – pay after ${nextHarvest}`
    };

    return (
        <div className="p-6 max-w-4xl mx-auto space-y-6">
            <Card title="1. Select Your Harvest Period">
                <Checkbox.Group
                    options={harvestOptions}
                    value={harvestMonths}
                    onChange={setHarvestMonths}
                />
                <p className="mt-2 text-gray-500 text-sm">
                    Select the months when you usually harvest. We use this to time your payments.
                </p>
            </Card>

            <Card title="2. Choose a Billing Method">
                <Radio.Group
                    onChange={(e) => setBillingType(e.target.value)}
                    value={billingType}
                >
                    <Radio value="monthly">Monthly</Radio>
                    <Radio value="perSeason">Per Season</Radio>
                    <Radio value="postHarvest">Post-Harvest</Radio>
                </Radio.Group>
                <p className="mt-2 text-gray-500 text-sm">
                    Choose the plan that matches your cash flow.
                </p>
            </Card>

            {billingType && (
                <Card title="3. Your Payment Plan Summary">
                    <Alert
                        type="info"
                        message={
                            billingLabel[billingType] || "Select billing type to preview schedule"
                        }
                        description={
                            billingType === "postHarvest"
                                ? `We’ll remind you to pay in the month after your ${nextHarvest} harvest.`
                                : null
                        }
                        showIcon
                    />
                </Card>
            )}

            <Card title="4. Select Payment Method">
                <Radio.Group
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    value={paymentMethod}
                    buttonStyle="solid"
                >
                    <Radio.Button value="Credit Card">
                        <CreditCardOutlined /> Credit Card
                    </Radio.Button>
                    <Radio.Button value="PayPal">
                        <DollarOutlined /> PayPal
                    </Radio.Button>
                    <Radio.Button value="Bank Transfer">
                        <BankOutlined /> Bank Transfer
                    </Radio.Button>
                </Radio.Group>
                <p className="mt-2 text-gray-500 text-sm">
                    Your selected method will be used for future payments.
                </p>

                {paymentMethod === "Credit Card" && (
                    <Form
                        form={form}
                        layout="vertical"
                        style={{ marginTop: 20 }}
                    >
                        <Form.Item label="Card Holder Name" name="cardHolder">
                            <Input placeholder="e.g. John Doe" />
                        </Form.Item>
                        <Form.Item label="Card Number" name="cardNumber">
                            <Input placeholder="e.g. 4111 1111 1111 1111" maxLength={19} />
                        </Form.Item>
                        <Space size="middle">
                            <Form.Item label="Expiry Month" name="expiryMonth">
                                <Select placeholder="MM">
                                    {[...Array(12).keys()].map((m) => (
                                        <Select.Option key={m + 1} value={String(m + 1).padStart(2, "0")}>
                                            {String(m + 1).padStart(2, "0")}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Expiry Year" name="expiryYear">
                                <Select placeholder="YYYY">
                                    {[...Array(10).keys()].map((y) => (
                                        <Select.Option key={2025 + y} value={2025 + y}>
                                            {2025 + y}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="CVC" name="cvc">
                                <Input maxLength={4} placeholder="123" style={{ width: 80 }} />
                            </Form.Item>
                        </Space>
                    </Form>
                )}
            </Card>

            <div className="text-right">
                <Button
                    type="primary"
                    disabled={!billingType || harvestMonths.length === 0 || !paymentMethod}
                >
                    Save Preferences
                </Button>
            </div>
        </div>
    );
}
